import React from "react";
import { connect } from "react-redux";
// import { Field, reduxForm } from "redux-form";
import { reduxForm } from "redux-form";

import { Link, Redirect, withRouter } from "react-router-dom";

import { Form, Button } from "react-bootstrap";

import { withT } from "services/translation/";

import get from "lodash/get";
import omit from "lodash/omit";

import {
  loginSuccess,
  loginFailed,
  loginStart,
  loginCancel,
  loginTotp,
  loginTotpFailed,
  loginTotpReset,
  // logoutRequest
} from "services/redux/actions";

import conxtdOut from "apis/conxtdOut";

import LoadingButton from "../LoadingButton";
import RecoveryCodes from "components/Common/RecoveryCodes/RecoveryCodes";

import ErrorBadge from "../Layout/ErrorBadge";

import "./LoginForm.scss";

class LoginForm extends React.Component {
  state = {
    error: {},
    credentials: {},
    intervalId: null,
    countdownSeconds: 0,
    startCountdown: null,
    resendRequests: 0,
    // Authenticator app users who have lost their app can fall back to one of
    // the recovery codes issued during setup
    useRecoveryCode: false,
    // A fresh set of codes, issued by the backend when the user signs in with
    // the last one they had left. Held here rather than in the store so they
    // never reach redux-persist, and shown before we let the redirect through.
    newRecoveryCodes: [],
  }

  componentDidMount = () => {
    this.props.dispatch(loginTotpReset());
  }

  componentDidUpdate = (prevProps, prevState) => {
    const { totp, totp_type } = this.props.user;
    const { startCountdown } = this.state;

    if ((!prevProps.user.totp && totp && totp_type === "E")) {
      this.setState({ startCountdown: 30 });
    }

    // Dropping out of the code step (Back, or too many attempts) returns to
    // email/password, so don't leave recovery code mode armed for next time
    if (prevProps.user.totp && !totp && this.state.useRecoveryCode) {
      this.setState({ useRecoveryCode: false });
    }

    if (!!startCountdown && !prevState.startCountdown) {
      this.setState({ 
        countdownSeconds: startCountdown,
        startCountdown: null,
      });

      const intervalId = setInterval(() => {
        this.setState({ countdownSeconds: this.state.countdownSeconds - 1 });
      }, 1000);
      this.setState({ intervalId });
    }   
  }

  componentWillUnmount() {
    const { intervalId } = this.state;
    if (intervalId) clearInterval(intervalId);
  }

  handleSubmit = (e) => {
    e.preventDefault();

    this.setState({
      error: {}
    });

    if (this.props.user?.totp) {
      // need to submit the totp code for validation - or, where the user has
      // lost their authenticator app, one of their recovery codes instead
      const { useRecoveryCode } = this.state;

      this.dispatchLoginWithCode(
        { ...this.state.credentials },
        e.target.totp.value,
        useRecoveryCode,
      );

    } else {
      let credentials = {};

      document.querySelectorAll("#LoginForm input").forEach(item => {
        if (item.name === "confirm_password") return;
        credentials[item.name] = item.value;
      });
      credentials.persist = document.getElementById("persist").checked;

      this.setState({ credentials: credentials});

      // Dispatch login event
      this.dispatchLogin(credentials);
    }
  };

  dispatchLogin = async credentials => {
    const { t } = this.props;

    this.props.dispatch(loginStart());

    // If no env if specified in url path, fallback to default
    const environment = this.props.match.params.env || conxtdOut.defaultEnv;

    try {
      const { user, enterprises, totp, totp_type, sites } = await conxtdOut.login(
        credentials,
        environment
      );

      if (totp) {
        this.props.dispatch(loginTotp({ totp, totp_type }));
      } else {
        this.props.dispatch(loginSuccess({ user, enterprises, sites }));
      }      
    } catch (error) {
      const status = get(error.response, "status");
      // 422 unprocessable
      if (status === 422) {
        // this.props.dispatch(
        //   loginFailed({
        //     message: t("errors.incorrect_credentials"),
        //   })
        // );
        if (error.response?.data?.message && error.response.data.message.includes("You have tried to login too many times. You can try again in")) {
          const secondsRemaining = parseInt(error.response.data.message.substring(61));

          if (!Number.isNaN(secondsRemaining)) {
            this.setState({
              error: {
                message: error.response.data.message.substring(0, 57),
                timeout: secondsRemaining,
              }
            });
          } else {
            this.setState({
              error: {
                message: t("errors.incorrect_credentials"),
              }
            });
          }
        } else {
          this.setState({
            error: {
              message: error.response?.data?.message || "Unknown error",
            }
          });
        }
        // Destroy session
        conxtdOut.logout();
        // this.props.dispatch(logoutRequest());
      } else {
        // this.props.dispatch(
        //   loginFailed({
        //     message: t("errors.login_failed_x", error.message),
        //   })
        // );
        this.setState({
          error: {
            message: t("errors.login_failed_x", error.message),
          }
        });
      }
      this.props.dispatch(
        loginFailed({}) // now just used to indicate that loading has stopped
      );
    }
  };

  dispatchLoginWithCode = async (credentials, code, useRecoveryCode) => {
    const { t } = this.props;

    this.props.dispatch(loginStart());

    // If no env if specified in url path, fallback to default
    const environment = this.props.match.params.env || conxtdOut.defaultEnv;

    try {
      // Recovery codes go to their own endpoint, but the response shape and
      // the error handling below are identical either way
      const { user, enterprises, sites } = useRecoveryCode
        ? await conxtdOut.loginWithRecoveryCode(credentials, environment, code)
        : await conxtdOut.loginWithCode(credentials, environment, code);

      // Spending the last remaining recovery code earns a fresh set. When that
      // happens recovery_codes is present in the response; otherwise the user
      // still has codes left and the backend omits it entirely. Stash them so
      // render() holds the user here until they've been saved.
      const reissuedCodes = (user && user.recovery_codes) || [];
      if (reissuedCodes.length > 0) {
        this.setState({ newRecoveryCodes: reissuedCodes });
      }

      // Codes are password-equivalent and only needed for the screen that
      // shows them, so keep them out of the store (and so out of localStorage)
      this.props.dispatch(
        loginSuccess({ user: omit(user, "recovery_codes"), enterprises, sites })
      );

    } catch (error) {
      const status = get(error.response, "status");
      // 422 unprocessable
      if (status === 422) {
        if (error.response?.data?.message && error.response.data.message.includes("too many times")) {
          const secondsRemaining = parseInt(error.response.data.message.substring(61));

          if (!Number.isNaN(secondsRemaining)) {
            this.setState({
              error: {
                message: error.response.data.message.substring(0, 57),
                timeout: secondsRemaining,
              }
            });
          } else {
            this.setState({
              error: {
                message: t("errors.incorrect_credentials"),
              }
            });
          }

          this.setState({ resendRequests: 0 });
          this.props.dispatch(loginTotpFailed());
        } else {
          this.setState({
            error: {
              message: error.response?.data?.message || "Unknown error",
            }
          });
        }
      } else {
        this.setState({
          error: {
            message: error.response?.data?.message || "Unknown error",
          }
        });
      }
      
      this.props.dispatch(loginCancel());
    }
  }

  render() {
    const { handleFocus, handleBlur, t, user } = this.props;

    // const error = user.error;
    const {
      error,
      credentials,
      countdownSeconds,
      intervalId,
      resendRequests,
      useRecoveryCode,
      newRecoveryCodes
    } = this.state;

    const session = conxtdOut.getSession();
    const loggedIn = session && session.sessionId;
    const loading = user.loading;
    const totp = user.totp;
    const totpType = user.totp_type;

    // If no env is specified in url path, fallback to default
    const environment = this.props.match.params.env || conxtdOut.defaultEnv;

    // The session is already established at this point, so this is the only
    // chance to show a freshly issued set of recovery codes - once we redirect
    // they're gone for good. Hold here until the user confirms they've saved
    // them, then fall through to the redirect below.
    if (newRecoveryCodes.length > 0) {
      return (
        <RecoveryCodes
          codes={newRecoveryCodes}
          description={t("recovery_codes_reissued_message")}
          onContinue={() => this.setState({ newRecoveryCodes: [] })}
        />
      );
    }

    // User is already logged in
    if (loggedIn && !loading) {
      if (user.enterprisesCount > 0 || user.sitesCount === 0) {
        // if the user has access to any enterprises, or has no access to anything, send them to the enterprises page
        return (
          <Redirect
            to={{
              pathname: "/enterprise",
              state: { from: this.props.location }
            }}
          />
        )
      } else {
        // if the user only has access to sites, send them to the sites page
        return (
          <Redirect
            to={{
              pathname: "/sites",
              state: { from: this.props.location }
            }}
          />
        )
      };
    }

    if (countdownSeconds === 0 && intervalId) {
      clearInterval(intervalId);
    }

    let environmentMessage = <>Error: invalid environment</>;
    if (environment && conxtdOut.BASE_URLS[environment]) {
      let switchToLink = "/login/staging";
      let switchToText = "Switch to Staging";
      
      if (environment === "staging") {
        switchToLink = "/login/live";
        switchToText = "Switch to Live";
      } else if (environment === "localhost8765" || environment === "localhost9980") {
        switchToLink = "/login/devbackend";
        switchToText = "Switch to Dev Backend";
      }

      environmentMessage = (
        <>
          Logging in to <b>{environment}</b>
          <Link to={switchToLink} className="waves-link">
            <Button size="sm" className="mx-2">
              {switchToText}
            </Button>
          </Link>
        </>
      );
    }
    let showEnvironment =
      this.props.match.params.env ||
      environment === "staging" ||
      environment === "stagingbackend" ||
      environment === "mars" ||
      environment === "devbackend" ||
      environment === "localhost8765" ||
      environment === "localhost9980"
        ? (
          <div className="alert alert-primary" role="alert">
            {environmentMessage}
          </div>
        )
        : null;

    return (
      <>
        {showEnvironment}
        {error.message && <ErrorBadge message={error.message} timeout={error.timeout} />}
        <Form
          onSubmit={this.handleSubmit}
          onFocus={handleFocus}
          onBlur={handleBlur}
          id="LoginForm"
          className="form-horizontal">
          {
            !totp &&
              <div className="form-group ">
                <Form.Control
                  name="email"
                  component="input"
                  type="email"
                  placeholder={t("email_address_placeholder")}
                />

                <Form.Control
                  name="password"
                  component="input"
                  type="password"
                  placeholder={t("password_placeholder")}
                />
              </div>
          }
          {
            totp &&
              <div className="form-group ">
                {totpType === "E" &&
                  <div style={{ width: "100%", fontSize: "20px", fontWeight: "bold" }} className="d-flex justify-content-center">
                    <div>{t("totp_email_message")}</div>
                  </div>
                }
                <div className="my-2">{
                  useRecoveryCode
                    ? t("recovery_code_message")
                    : totpType === "A"
                      ? t("totp_auth_app_code_message")
                      : t("totp_code_message") + credentials.email
                }</div>
                {/* Remounted on toggle so a part-typed code from the other
                    mode isn't left behind in this uncontrolled input */}
                <Form.Control
                  key={useRecoveryCode ? "recovery" : "totp"}
                  name="totp"
                  component="input"
                  type="text"
                  autoComplete="off"
                  placeholder={
                    useRecoveryCode
                      ? t("recovery_code_placeholder")
                      : t("totp_code_placeholder")
                  }
                />
              </div>
          }
          {
            !totp &&
              <div className="form-group row">
                <div className="col-6">
                  <Form.Check
                    type="checkbox"
                    name="persist"
                    id="persist"
                    component="input"
                    label={t("remember_me")}
                  />
                </div>
                <Link className="text-right col-6" to="/reset-password">
                  {t("lost_password")}
                </Link>
              </div>
          }
          <LoadingButton
            type="submit"
            className="btn btn-primary btn-block waves-effect"
            loading={loading}>
            {totp ? t("submit_code") : t("log_in")}
          </LoadingButton>
          {
            totp && 
              <div>
                <div
                  className="mt-4 d-flex justify-content-between align-items-center"
                  // style={{ height: "35px" }}
                >
                  <div>
                    <Link to="#"
                      onClick={() => {
                        if (intervalId) clearInterval(intervalId);
                        this.setState({ resendRequests: 0 });
                        this.props.dispatch(loginTotpFailed());
                      }}
                    >Back</Link>
                  </div>
                  {
                    totpType === "E" && countdownSeconds > 0 &&
                      <div>
                        {t("totp_resend_message")} {t("totp_resend_in_message")} {
                          totp && countdownSeconds > 0 && (countdownSeconds === 60 ? 60 :`${
                            String(countdownSeconds%60).padStart(2, '0')
                          }s`)
                        }
                      </div>
                  }
                  {
                    totpType === "E" && countdownSeconds === 0 && 
                      <div>
                        {t("totp_resend_message")} <Link to="#"
                          onClick={() => {
                            const updatedResendRequests = resendRequests + 1;
                            if (updatedResendRequests >= 5) {
                              this.setState({ resendRequests: 0 });
                              this.props.dispatch(loginTotpFailed());
                            } else {
                              this.dispatchLogin(credentials);
                              this.setState({
                                startCountdown: updatedResendRequests === 4 ? 60 : 30,
                                resendRequests: updatedResendRequests
                              });
                            }
                          }}
                        >{t("totp_resend")}</Link>
                      </div>
                  }
                  {
                    // Only the authenticator app method issues recovery codes;
                    // email 2FA users can just have another code sent instead.
                    totpType === "A" &&
                      <div>
                        <Link to="#"
                          onClick={() => {
                            this.setState({
                              useRecoveryCode: !useRecoveryCode,
                              error: {}
                            });
                          }}
                        >
                          {useRecoveryCode
                            ? t("totp_use_auth_app")
                            : t("totp_use_recovery_code")}
                        </Link>
                      </div>
                  }
                </div>
                <div>
                  {
                    totpType === "A" && useRecoveryCode &&
                      <div>
                        <hr />
                        <div className="d-flex justify-content-center">
                          <Link to="#"
                              onClick={() => {
                                window.Intercom('showArticle', 18242349); //17028746 for dev/staging, 18242349 for live
                                // window.open('https://intercom.help/conxtd-technologies-ltd-test/en/articles/17028746-2fa-recovery-codes');
                              }}
                            >
                              {t("recovery_code_help_message")}
                          </Link>
                        </div>
                      </div>
                  }
                </div>
              </div>
          }
          {/* 
          <div className="form-group mt-3 mb-0 row">
            <div className="col-12">
              <div className="float-right">
                <Link to="#" className="text-muted">
                  {t("request_invite")}
                </Link>
              </div>
            </div>
          </div> */}
        </Form>
      </>
    );
  }
}

LoginForm = reduxForm({
  form: "login"
})(LoginForm);

const mapStateToProps = state => {
  return { user: state.user };
};
export default withRouter(
  withT(connect(mapStateToProps)(LoginForm), "login_form")
);
